import React from 'react'
import { FormControl, Select, MenuItem, Stack, Typography, Box, alpha } from '@mui/material'
import { ExpandMore, Close as CloseIcon } from '@mui/icons-material'
import { Button as UIButton, getSortDirectionLabels, type SortFieldType } from '@repo/ui'
import { X as XIcon } from 'lucide-react'
import { getPickerMenuProps } from './pickerListStyle'
import { SORT_FIELD_TYPES } from 'utils/sortFieldTypes'
import { FIELD_HEIGHT, getFieldRadius } from './fieldMetrics'
import { border } from 'settings/tokens';

interface SortFieldProps {
  field: string
  direction: 'ASC' | 'DESC'
  onFieldChange: (field: string) => void
  onDirectionChange: (direction: 'ASC' | 'DESC') => void
  onClear: () => void
  showClear?: boolean
  availableFields?: string[]
  disabledFields?: string[]
  /**
   * Data type per sortable field, used to label the direction dropdown (VERA-480).
   * Without it every field fell back to "A - Z" / "Z - A" — meaningless on a DATE, where
   * the reader needs to know which end is the recent one. Omit and it degrades to text
   * labels, matching the platform's forgiving default.
   */
  fieldTypes?: Record<string, SortFieldType>
  /**
   * Display name per field key. Supply it and the built-in map below is not
   * consulted at all.
   *
   * The built-in map is the same drift `fieldTypes` had: it is a SECOND copy of
   * something the caller already knows, so a field added at the call site renders
   * its raw key here — `owned_since` appeared verbatim in the select the moment
   * "Ownership date" was added. `SortDrawer` derives this from its `options`, so
   * a field declared once carries both its label and its type.
   */
  fieldLabels?: Record<string, string>
  inline?: boolean
  mobileMode?: boolean
  /** 1-based order shown in a small badge next to the field (sort precedence). */
  sortNumber?: number
}

/**
 * ⚠️ The radius is CLAMPED, like every other field — it was a hardcoded `'12px'`
 * (and `'12px !important'` on the second selector), which is precisely the
 * bypass `getFieldRadius` exists to prevent. MEASURED in the mobile Sort sheet:
 * the two selects rendered at 12px beside a 6px search field and a 6px date
 * trigger in the sheet one layer behind them, so the roundest control on screen
 * was the one nobody had made a decision about.
 *
 * See FIELD_RADIUS_MAX for why a field's radius is not the brand's verbatim:
 * radius does not scale with width, so the value that flatters a 44px button
 * turns a 343px-wide control into a lozenge.
 */
const fieldRadius = (theme: any) => `${getFieldRadius(theme?.shape?.borderRadius)}px`

const selectStyles = {
  '&& .MuiOutlinedInput-root': {
    borderRadius: fieldRadius,
    backgroundColor: (theme: any) => theme.palette.grey[50],
    height: `${FIELD_HEIGHT}px`,
    marginTop: '0 !important',
    '& .MuiOutlinedInput-notchedOutline, & fieldset': {
      borderColor: border.subtle,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: border.subtle,
      borderWidth: '1px',
    },
    '&:hover': {
      backgroundColor: (theme: any) => theme.palette.grey[50],
      '& .MuiOutlinedInput-notchedOutline, & fieldset': {
        borderColor: border.subtle,
      },
    },
    '&.Mui-focused': {
      backgroundColor: (theme: any) => theme.palette.grey[50],
      '& .MuiOutlinedInput-notchedOutline, & fieldset': {
        borderColor: border.subtle,
        borderWidth: '1px',
      },
    },
    '& .MuiSelect-select': {
      padding: '8px 12px',
      // `text.primary`, not a hardcoded `#000000` — the tenant owns its text
      // colour (see `settings/tokens.ts`, which lists exactly this substitution).
      color: 'text.primary',
    },
  },
  '&& .MuiInputBase-root.MuiOutlinedInput-root.MuiSelect-root': {
    borderRadius: (theme: any) => `${fieldRadius(theme)} !important`,
    marginTop: '0 !important',
    color: 'text.primary',
  },
  '& .MuiInputLabel-root': { display: 'none' },
  marginTop: '0 !important',
}

// Platform select parity: rounded per-item hover highlight INSET from the paper edge —
// never a full-width bleed. Shared across every dropdown/picker via pickerListStyle
// (VERA-291), and neutral (no brand/purple tint) since these render inside brand sites.
const menuProps = getPickerMenuProps()

export const SortField: React.FC<SortFieldProps> = ({
  field,
  direction,
  onFieldChange,
  onDirectionChange,
  onClear,
  showClear = true,
  availableFields = ['name', 'status', 'last_activity', 'last_scan'],
  disabledFields = [],
  fieldTypes,
  fieldLabels,
  inline = false,
  mobileMode = false,
  sortNumber,
}) => {
  const directionLabels = getSortDirectionLabels(fieldTypes?.[field] ?? SORT_FIELD_TYPES[field])

  const fieldSelect = (
    <FormControl fullWidth size="small" sx={{ ...selectStyles, minWidth: inline ? 180 : undefined, '& .MuiInputBase-root.MuiOutlinedInput-root.MuiSelect-root': { marginTop: '0 !important' } }}>
      <Select
        value={field}
        onChange={(e) => onFieldChange(e.target.value)}
        displayEmpty
        IconComponent={ExpandMore}
        inputProps={{ 'aria-label': 'Sort field' }}
        MenuProps={menuProps}
      >
        {availableFields.map((fieldValue) => {
          // Fallback only — for callers that predate `fieldLabels` (the desktop
          // sort popover/menu). Anything routed through `SortDrawer` passes its
          // own and never reaches this.
          const builtInLabels = {
            name: 'Certificate name',
            status: 'Status',
            last_activity: 'Last activity date',
            last_scan: 'Last scan',
            owner_name: 'Owner name',
            date: 'Date',
            sell_price: 'Sell price',
            // VERA-315: Scans sorts by location. Without this the desktop configure
            // popover — which supplies no `fieldLabels` — rendered the raw key
            // "location" in its select, the same defect the comment above records
            // for `owned_since`.
            location: 'Location',
            // VERA-978: Activity sorts by event type. Unlabelled keys fall through
            // to the raw value, which would render "type" in the select.
            type: 'Event type',
          } as const
          return (
            <MenuItem key={fieldValue} value={fieldValue} disabled={disabledFields.includes(fieldValue) && fieldValue !== field}>
              {fieldLabels?.[fieldValue] ??
                builtInLabels[fieldValue as keyof typeof builtInLabels] ??
                fieldValue}
            </MenuItem>
          )
        })}
      </Select>
    </FormControl>
  )

  const directionSelect = (
    <FormControl fullWidth size="small" sx={{ ...selectStyles, minWidth: inline ? 120 : undefined, '& .MuiInputBase-root.MuiOutlinedInput-root.MuiSelect-root': { marginTop: '0 !important' } }}>
      <Select
        value={direction}
        onChange={(e) => onDirectionChange(e.target.value as 'ASC' | 'DESC')}
        displayEmpty
        IconComponent={ExpandMore}
        inputProps={{ 'aria-label': 'Sort direction' }}
        MenuProps={menuProps}
      >
        {/* Labels follow the FIELD'S TYPE, never a fixed pair. An explicit `fieldTypes`
            wins; otherwise the canonical registry supplies it, so every sort surface is
            fixed at once rather than one drawer at a time. Sorting by date now reads
            "Newest first" instead of the nonsensical "A - Z" (VERA-480). */}
        <MenuItem value="ASC">{directionLabels[0]}</MenuItem>
        <MenuItem value="DESC">{directionLabels[1]}</MenuItem>
      </Select>
    </FormControl>
  )

  if (inline) {
    return (
      <Stack spacing={0} direction="row" alignItems="center" sx={{ gap: 1, '& > * + *': { marginLeft: 0, marginTop: '0 !important' }, '& .MuiFormControl-root': { marginTop: '0 !important' } }}>
        {fieldSelect}
        {directionSelect}
        {showClear && (
          <Box onClick={onClear} sx={{ ml: 1 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '8px', backgroundColor: (theme: any) => theme.palette.grey[50], display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', '&:hover': { backgroundColor: border.subtle } }}>
              <CloseIcon fontSize="small" />
            </Box>
          </Box>
        )}
      </Stack>
    )
  }

  // Mobile: a small header row (number badge on the left, compact Remove icon on the
  // right), then the field + direction selects full-width and aligned below it. The
  // compact icon replaces a full-width "Remove" button — with several sorts stacked,
  // three prominent Remove buttons read as louder than the sorts themselves.
  return (
    // flexbox `gap` (not Stack `spacing`) — the selects carry `marginTop: 0 !important`
    // which would override margin-based spacing, so the dropdowns would touch.
    <Stack sx={{ gap: '14px', '& .MuiFormControl-root': { marginTop: '0 !important' } }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ minHeight: 28, px: 0.5 }}
      >
        {sortNumber != null ? (
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: '6px',
              backgroundColor: (theme: any) => theme.palette.grey[100],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', lineHeight: 1 }}>
              {sortNumber}
            </Typography>
          </Box>
        ) : (
          <Box />
        )}
        {showClear && (
          <Box
            onClick={onClear}
            role="button"
            tabIndex={0}
            aria-label="Remove sort"
            sx={{
              // X, not a trash can. The test (working-conventions §38) is "does a row
              // disappear from the database?" — removing a sort criterion severs
              // nothing and is undone by re-adding it, so a trash can overstates the
              // consequence and makes a free action feel dangerous.
              //
              // Neutral at rest, destructive on hover AND focus (§32): this control is
              // persistent chrome, so permanent red would make the least important
              // thing in the row the loudest. Focus is included so keyboard users get
              // the same cue as the cursor.
              //
              // The red stays on the ICON. The line is the control's WEIGHT, not
              // whether the action destroys anything (user directive, 2026-08): a
              // bare clear icon may redden on reach, a full labelled `Reset` button
              // may not — see `SortConfigPopover`'s Reset.
              width: 32,
              height: 32,
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'text.secondary',
              backgroundColor: 'transparent',
              transition: 'background-color .15s, color .15s',
              '&:hover, &:focus-visible': {
                outline: 'none',
                color: (theme: any) => theme.palette.error.main,
                backgroundColor: (theme: any) => alpha(theme.palette.error.main, 0.08),
              },
            }}
          >
            <XIcon size={18} />
          </Box>
        )}
      </Stack>
      {fieldSelect}
      {directionSelect}
    </Stack>
  )
}
